import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useTennesseeMemphisDemoEnabled } from 'utils/featureFlags';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import {
  isTnngMemphisResourceSet,
  TNNG_MEMPHIS_FACILITIES,
  tnngMemphisResourceNotesMarker,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/tennesseeMemphisThreatNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

async function patchMissingTnngSymbology({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const missing = resources.filter(
    (resource) => isTnngMemphisResourceSet(resource) && !resource.symbology
  );
  if (missing.length === 0) return false;

  await Promise.all(
    missing.map((resource) => {
      const facility = TNNG_MEMPHIS_FACILITIES.find((item) =>
        resource.notes?.endsWith(item.id)
      );
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: facility?.symbology || 'facility',
      };
      const { members: _members, ...dataForBackend } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureTennesseeMemphisResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isTnngMemphisResourceSet)
      .map((resource) => resource.notes as string)
  );

  const toCreate = TNNG_MEMPHIS_FACILITIES.filter(
    (facility) =>
      !existingMarkers.has(tnngMemphisResourceNotesMarker(facility.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    await Promise.all(
      toCreate.map((facility) => {
        const notes = tnngMemphisResourceNotesMarker(facility.id);
        return axios.post(config.apiGateway.ics_resource_sets, {
          name: `${facility.name} (${facility.city}, ${facility.state})`,
          description: facility.risk,
          kind: facility.kind,
          type: facility.type,
          quantity: 1,
          status: 'In Organization',
          asset_status: 'PMC',
          asset_status_timestamp: new Date().toISOString(),
          address: `${facility.city}, ${facility.state}`,
          symbology: facility.symbology,
          notes,
          geometry: {
            type: 'Point',
            coordinates: [facility.lon, facility.lat],
          },
          incidents: [],
          archived: false,
          group_guid: groupGuid,
          user_guid: userGuid,
          incident_id: null,
        });
      })
    );
    didMutate = true;
  }

  const patched = await patchMissingTnngSymbology({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

/** When TNG-only, ensure Memphis-area TNNG facilities exist as resource sets. */
export function useEnsureTennesseeMemphisResourceSets() {
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tennesseeMemphisDemo || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureTennesseeMemphisResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[TNNG Memphis] Failed to ensure Memphis resource sets',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [tennesseeMemphisDemo, groupGuid, userGuid, queryClient]);
}
